import { useEffect, useState } from 'react'
import { api } from '../api'
import { Card, Button, Form, Input, Select, DatePicker, Tag, Empty, Spin, Space, Typography, Drawer, Checkbox, InputNumber } from 'antd'
import { PlusOutlined, DeleteOutlined, CalendarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useIsMobile from '../hooks/useIsMobile'

const { Title, Text } = Typography
const { TextArea } = Input

const TIPOS = [
  'Tratamiento fitosanitario',
  'Fertilización',
  'Riego',
  'Siembra',
  'Recolección',
  'Laboreo',
  'Poda',
  'Otro',
]

const COLORES_TIPO = {
  'Tratamiento fitosanitario': 'red',
  'Fertilización': 'gold',
  'Riego': 'blue',
  'Siembra': 'green',
  'Recolección': 'purple',
  'Laboreo': 'orange',
  'Poda': 'cyan',
  'Otro': 'default',
}

export default function CuadernoCampo() {
  const isMobile = useIsMobile()
  const [registros, setRegistros] = useState([])
  const [parcelas, setParcelas] = useState([])
  const [productos, setProductos] = useState([])
  const [mostrarForm, setMostrarForm] = useState(false)
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [prodsSeleccionados, setProdsSeleccionados] = useState([])
  const [form] = Form.useForm()

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    try {
      const [trats, parcs] = await Promise.all([
        api('/tratamientos'),
        api('/parcelas'),
      ])
      setRegistros(trats)
      setParcelas(parcs)
      // Cargar productos solo si existen
      try {
        const prods = await api('/productos')
        setProductos(prods)
      } catch { setProductos([]) }
    } catch (err) {
      console.error('Error cargando cuaderno:', err)
    }
    setCargando(false)
  }

  const abrirForm = () => {
    form.resetFields()
    setProdsSeleccionados([])
    setMostrarForm(true)
  }

  const guardar = async (values) => {
    setGuardando(true)
    try {
      const productosEnviar = prodsSeleccionados.map(p => ({
        producto_id: p.id,
        cantidad: p.cantidad || 0,
        unidad: p.unidad || 'L',
      }))
      await api('/tratamientos', {
        method: 'POST',
        body: JSON.stringify({
          fecha: values.fecha.format('YYYY-MM-DD'),
          tipo: values.tipo,
          descripcion: values.descripcion || '',
          notas: values.notas || '',
          parcelas: values.parcelas,
          productos: productosEnviar,
        }),
      })
      setMostrarForm(false)
      form.resetFields()
      setProdsSeleccionados([])
      cargar()
    } catch (err) {
      console.error('Error guardando registro:', err)
    }
    setGuardando(false)
  }

  const eliminar = async (id) => {
    try {
      await api(`/tratamientos/${id}`, { method: 'DELETE' })
      cargar()
    } catch (err) {
      console.error('Error eliminando registro:', err)
    }
  }

  const toggleProducto = (prod, checked) => {
    if (checked) {
      setProdsSeleccionados([...prodsSeleccionados, { ...prod, cantidad: 1, unidad: 'L' }])
    } else {
      setProdsSeleccionados(prodsSeleccionados.filter(p => p.id !== prod.id))
    }
  }

  const actualizarCantidadProd = (prodId, cantidad) => {
    setProdsSeleccionados(prodsSeleccionados.map(p => p.id === prodId ? { ...p, cantidad } : p))
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B3A6B 0%, #2E5FA3 100%)', padding: isMobile ? '14px 14px' : '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
        <Title level={4} style={{ color: 'white', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Cuaderno de campo</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={abrirForm}
          style={{ background: '#2E7D32', flexShrink: 0 }}
        >
          {!isMobile && 'Nuevo'}
        </Button>
      </div>

      <Drawer
        title="Nuevo registro"
        open={mostrarForm}
        onClose={() => { setMostrarForm(false); form.resetFields(); setProdsSeleccionados([]) }}
        width={isMobile ? '100%' : 400}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => { setMostrarForm(false); form.resetFields(); setProdsSeleccionados([]) }}>
              Cancelar
            </Button>
            <Button type="primary" loading={guardando} onClick={() => form.submit()}>
              Guardar registro
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={guardar} initialValues={{ fecha: dayjs(), tipo: TIPOS[0] }}>
          <Form.Item label="Fecha" name="fecha" rules={[{ required: true, message: 'Selecciona una fecha' }]}>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item label="Tipo de actividad" name="tipo" rules={[{ required: true }]}>
            <Select>
              {TIPOS.map(t => <Select.Option key={t} value={t}>{t}</Select.Option>)}
            </Select>
          </Form.Item>

          <Form.Item label="Parcelas" name="parcelas" rules={[{ required: true, message: 'Selecciona al menos una parcela' }]}>
            <Select mode="multiple" placeholder="Selecciona parcelas">
              {parcelas.map(p => (
                <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Descripcion" name="descripcion">
            <TextArea rows={2} placeholder="Descripcion de la actividad..." />
          </Form.Item>

          <Form.Item label="Notas" name="notas">
            <TextArea rows={2} placeholder="Observaciones adicionales..." />
          </Form.Item>

          {productos.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>Productos utilizados</Text>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {productos.map(prod => {
                  const seleccionado = prodsSeleccionados.find(p => p.id === prod.id)
                  return (
                    <Card key={prod.id} size="small" style={{ borderRadius: '8px' }}>
                      <Checkbox
                        checked={!!seleccionado}
                        onChange={(e) => toggleProducto(prod, e.target.checked)}
                      >
                        <Text style={{ fontSize: '13px' }}>{prod.nombre}</Text>
                        <Text type="secondary" style={{ fontSize: '11px', marginLeft: '8px' }}>
                          (Stock: {prod.stock_actual})
                        </Text>
                      </Checkbox>
                      {seleccionado && (
                        <div style={{ marginTop: '8px', marginLeft: '24px' }}>
                          <InputNumber
                            size="small"
                            min={0.1}
                            step={0.5}
                            value={seleccionado.cantidad}
                            onChange={(v) => actualizarCantidadProd(prod.id, v)}
                            style={{ width: '80px' }}
                          />
                          <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                            {prod.unidad || 'L'}
                          </Text>
                        </div>
                      )}
                    </Card>
                  )
                })}
              </Space>
            </div>
          )}
        </Form>
      </Drawer>

      <div style={{ padding: '16px' }}>
        {cargando ? (
          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <Spin size="large" />
          </div>
        ) : registros.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4}>
                <Text type="secondary">No hay registros en el cuaderno</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Pulsa "Nuevo" para crear el primer registro</Text>
              </Space>
            }
            style={{ marginTop: '60px' }}
          />
        ) : (
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            {registros.map((r) => (
              <Card key={r.id} size="small" style={{ borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <Tag color={COLORES_TIPO[r.tipo] || 'default'}>{r.tipo}</Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <CalendarOutlined /> {dayjs(r.fecha).format('DD/MM/YYYY')}
                      </Text>
                    </div>
                    {r.descripcion && (
                      <Text style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>{r.descripcion}</Text>
                    )}
                    {r.parcelas && r.parcelas.length > 0 && r.parcelas[0].id && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        Parcelas: {r.parcelas.map(p => p.nombre).join(', ')}
                      </Text>
                    )}
                    {r.productos && r.productos.length > 0 && r.productos[0].id && (
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Productos: {r.productos.map(p => `${p.nombre} (${p.cantidad} ${p.unidad || ''})`).join(', ')}
                        </Text>
                      </div>
                    )}
                    {r.notas && (
                      <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                        {r.notas}
                      </Text>
                    )}
                  </div>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => eliminar(r.id)}
                  />
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>
    </div>
  )
}
